import React, { useCallback, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';

const fadeIn = keyframes`from{opacity:0;transform:translateY(2px)}to{opacity:1;transform:translateY(0)}`;

const Bubble = styled.div<{ $x: number; $y: number; $maxWidth?: number }>`
  position: fixed;
  left: ${({ $x }) => $x}px;
  top: ${({ $y }) => $y}px;
  z-index: 99999;
  padding: 5px 10px;
  border-radius: 6px;
  background: var(--bg-app);
  border: 1px solid var(--border-subtle);
  color: var(--text-secondary);
  font-size: 11px;
  font-weight: 500;
  white-space: ${({ $maxWidth }) => ($maxWidth ? 'normal' : 'nowrap')};
  max-width: ${({ $maxWidth }) => ($maxWidth ? `${$maxWidth}px` : 'none')};
  pointer-events: none;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  animation: ${fadeIn} 0.1s ease;
  line-height: 1.45;
`;

type Props = {
  content: React.ReactNode;
  children: React.ReactElement;
  delay?: number;
  placement?: 'top' | 'bottom';
  maxWidth?: number;
};

export const Tooltip: React.FC<Props> = ({ content, children, delay = 400, placement = 'bottom', maxWidth }) => {
  const [show, setShow] = useState(false);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const ref = useRef<HTMLElement>(null);

  const handleEnter = useCallback(() => {
    timer.current = setTimeout(() => {
      if (!ref.current) return;
      const r = ref.current.getBoundingClientRect();
      const x = r.left + r.width / 2;
      const y = placement === 'top' ? r.top - 6 : r.bottom + 6;
      setPos({ x, y });
      setShow(true);
    }, delay);
  }, [delay, placement]);

  const handleLeave = useCallback(() => {
    clearTimeout(timer.current);
    setShow(false);
  }, []);

  const child = React.cloneElement(children, {
    ref,
    onMouseEnter: handleEnter,
    onMouseLeave: handleLeave,
    onFocus: handleEnter,
    onBlur: handleLeave,
  } as React.HTMLAttributes<HTMLElement>);

  return (
    <>
      {child}
      {show && content && createPortal(
        <Bubble
          $x={pos.x}
          $y={pos.y}
          $maxWidth={maxWidth}
          style={{ transform: placement === 'top' ? 'translate(-50%, -100%)' : 'translate(-50%, 0)' }}
        >
          {content}
        </Bubble>,
        document.body,
      )}
    </>
  );
};
